import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import {
  SHARE_WIN,
  RESERVE_WIN,
  REFI_WIN,
  HOME_INSURANCE_WIN,
  ESS_WIN,
  NEW_HOME_WIN,
  SHOULD_YOU_SELL_WIN,
} from '../../../constants/wins'
import {
  isTouchDevice,
  pushToWindowResize,
  removeFromWindowResize,
} from '../../../helpers/browser'
import { useUniqueId } from '../../../hooks'

import {
  selectShouldRenderEquitySmartSearchFeatures,
  selectShouldRenderHomeInsuranceModule,
  selectShouldRenderEmergencyReserve,
  selectShouldRenderRefiOptions,
  selectShouldRenderCashout,
  selectShouldDisplayShouldYouSell,
} from '../../../store/selectors/display'

import Carousel from '../Carousel'
import HomeInsurance from './Wins/HomeInsurance'
import Reserve from './Wins/Reserve'
import Refi from './Wins/Refi'
import Ess from './Wins/Ess'
import Share from './Wins/Share'
import NewHome from './Wins/NewHome'
import ShouldYouSell from './Wins/ShouldYouSell'

import styles from './ImportantNowCarousel.module.scss'

const wideBreak = styles.wideBreak
const mediumBreak = styles.mediumBreak

const WIN_COMPONENT_MAPPING = {
  [HOME_INSURANCE_WIN]: {
    component: HomeInsurance,
    displaySelector: selectShouldRenderHomeInsuranceModule,
  },
  [REFI_WIN]: {
    component: Refi,
    displaySelector: selectShouldRenderRefiOptions,
  },
  [SHOULD_YOU_SELL_WIN]: {
    component: ShouldYouSell,
    displaySelector: selectShouldDisplayShouldYouSell,
  },
  [NEW_HOME_WIN]: {
    component: NewHome,
    displaySelector: selectShouldRenderCashout,
  },
  [RESERVE_WIN]: {
    component: Reserve,
    displaySelector: selectShouldRenderEmergencyReserve,
  },
  [ESS_WIN]: {
    component: Ess,
    displaySelector: selectShouldRenderEquitySmartSearchFeatures,
  },
  [SHARE_WIN]: {
    component: Share,
    displaySelector: () => true,
  },
}

const ImportantNowCarousel = ({ recommendations, setHideParentPanel }) => {
  const id = useUniqueId('ImportantNowCarousel_')
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  const wins = Object.keys(WIN_COMPONENT_MAPPING).map(key => {
    const { component: Component, displaySelector } = WIN_COMPONENT_MAPPING[key]

    // DANGER: useSelector in a "callback" isn't great, but
    //   since WIN_COMPONENT_MAPPING is a constant with fixed length
    //   we will always fire this hook the same way each render cycle
    const display = useSelector(displaySelector) // eslint-disable-line
    const recommendation = recommendations.find(
      r => r.recommendationCode === key
    )
    const priority = recommendation ? recommendation.priority : null

    return (
      <Component
        key={`Slide_${key}`}
        show={recommendation && display}
        priority={priority}
      />
    )
  })

  const slides = wins
    .filter(c => c.props.show)
    .sort((a, b) => {
      const A = a.props.priority
      const B = b.props.priority

      return A && (!B || A < B) ? -1 : 1
    })
    .slice(0, 3)

  useEffect(() => {
    setHideParentPanel(slides.length < 2)
  }, [slides.length, setHideParentPanel])

  useEffect(() => {
    pushToWindowResize(id, () => setWindowWidth(window.innerWidth))
    return () => removeFromWindowResize(id)
  }, [id])

  const responsive = [
    {
      breakpoint: parseInt(wideBreak, 10),
      settings: {
        slidesToShow: 2,
        arrows: slides.length > 2,
        dots: slides.length > 2,
      },
    },
  ]

  if (!isTouchDevice()) {
    responsive.push({
      breakpoint: parseInt(mediumBreak, 10),
      settings: {
        slidesToShow: 1,
        arrows: slides.length > 1,
        dots: slides.length > 1,
      },
    })
  } else {
    responsive.push({
      breakpoint: parseInt(mediumBreak, 10),
      settings: {
        slidesToShow: 2,
        arrows: false,
        dots: slides.length > 1,
      },
    })
    if (slides.length > 1 && windowWidth < parseInt(mediumBreak, 10)) {
      slides.push(<span className={styles.dummySpan} />)
    }
  }

  return (
    <Carousel
      className={classnames(
        styles.carousel,
        slides.length === 1 && styles.singleSlide
      )}
      slides={slides}
      speed={300}
      swipeToSlide
      slidesToScroll={1}
      rows={1}
      responsive={responsive}
      touchThreshold={7.5}
    />
  )
}

ImportantNowCarousel.propTypes = {
  recommendations: PropTypes.arrayOf(
    PropTypes.shape({
      metadata: PropTypes.object,
      recommendationCode: PropTypes.string,
    })
  ),
  setHideParentPanel: PropTypes.func,
}

export default ImportantNowCarousel
